import React, { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import Chart from "chart.js/auto";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";

type TrendPoint = { date: string; value: number | null };
type Metric = {
    key: string;
    label: string;
    latest: number | null;
    latestDate: string | null;
    deltaVsPrevious: number | null;
    trend: TrendPoint[];
    error?: boolean;
};

const formatPct = (v: number | null) => (v == null ? "—" : `${v.toFixed(2)}%`);

const formatDate = (d: string | null) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");

const VitalCard: React.FC<{ metric: Metric }> = ({ metric }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const chartRef = useRef<Chart | null>(null);

    useEffect(() => {
        if (!canvasRef.current || metric.error || metric.latest == null) return;
        chartRef.current?.destroy();
        chartRef.current = new Chart(canvasRef.current, {
            type: "line",
            data: {
                labels: metric.trend.map((p) => formatDate(p.date)),
                datasets: [
                    {
                        data: metric.trend.map((p) => p.value),
                        borderColor: "#264164",
                        backgroundColor: "rgba(38, 65, 100, 0.08)",
                        fill: true,
                        tension: 0.3,
                        pointRadius: 0,
                        spanGaps: true,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: "#1f2937",
                        padding: 8,
                        callbacks: { label: (ctx) => `${(ctx.parsed.y ?? 0).toFixed(2)}%` },
                    },
                },
                scales: {
                    y: { beginAtZero: true, ticks: { color: "#9ca3af", font: { size: 10 }, callback: (v) => `${v}%` } },
                    x: { grid: { display: false }, ticks: { color: "#9ca3af", font: { size: 10 }, maxTicksLimit: 6 } },
                },
            },
        });
        return () => {
            chartRef.current?.destroy();
            chartRef.current = null;
        };
    }, [metric]);

    const delta = metric.deltaVsPrevious;
    const deltaColor = delta == null ? "#9ca3af" : delta > 0 ? "#dc2626" : delta < 0 ? "#16a34a" : "#9ca3af";
    const deltaLabel = delta == null ? "" : `${delta > 0 ? "+" : ""}${delta.toFixed(2)}pp vs previous day`;

    return (
        <div className="card">
            <Card>
                <p className="m-0 text-sm font-semibold" style={{ color: "#264164" }}>{metric.label}</p>
                {metric.error ? (
                    <p className="m-0 mt-2 text-xs" style={{ color: "#9ca3af" }}>Data unavailable for this app</p>
                ) : metric.latest == null ? (
                    <p className="m-0 mt-2 text-xs" style={{ color: "#9ca3af" }}>No data recorded</p>
                ) : (
                    <>
                        <div className="flex items-end gap-2 mt-1">
                            <span className="text-2xl font-bold" style={{ color: "#111827" }}>{formatPct(metric.latest)}</span>
                            {delta != null && <span className="text-xs mb-1" style={{ color: deltaColor }}>{deltaLabel}</span>}
                        </div>
                        <p className="m-0 text-xs" style={{ color: "#9ca3af" }}>as of {formatDate(metric.latestDate)}</p>
                        <div style={{ height: "110px", marginTop: "8px" }}>
                            <canvas ref={canvasRef} />
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

const PlayVitalsPage: React.FC = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState<Metric[]>([]);
    const [windowLabel, setWindowLabel] = useState("");

    const fetchVitals = async () => {
        setLoading(true);
        try {
            const res = await apiFunctions.get("/playvitals");
            if (res?.data?.success) {
                setMetrics(res.data.data.metrics || []);
                const s = res.data.data.windowStart;
                const e = res.data.data.windowEnd;
                if (s && e) {
                    const fmt = (d: any) => new Date(d.year, d.month - 1, d.day).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
                    setWindowLabel(`${fmt(s)} – ${fmt(e)}`);
                }
            } else {
                showToast({ severity: "error", summary: "Error", detail: res?.data?.message || "Failed to load vitals", life: 3000 });
            }
        } catch (err) {
            console.error("Failed to load Play vitals:", err);
            showToast({ severity: "error", summary: "Error", detail: "Failed to load vitals", life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVitals();
    }, []);

    return (
        <div className="dashboard-margin">
            <div className="grid grid-cols-12 gap-4 mt-4 items-center">
                <div className="col-span-12 md:col-span-8">
                    <h2 className="card-title m-0">Play Store Vitals</h2>
                    <p className="text-xs m-0 mt-1" style={{ color: "#000000" }}>
                        Android technical health metrics from Google Play{windowLabel && ` — ${windowLabel}`}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-4 mt-4">
                {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                        <div className="col-span-12 md:col-span-6 xl:col-span-4" key={i}>
                            <div className="card"><Card><Skeleton height="160px" /></Card></div>
                        </div>
                    ))
                ) : (
                    metrics.map((m) => (
                        <div className="col-span-12 md:col-span-6 xl:col-span-4" key={m.key}>
                            <VitalCard metric={m} />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default PlayVitalsPage;
